'use client'

import { useContext, useEffect, useState, useCallback, useMemo } from 'react'
import { AuthContext } from '@/contexts/AuthContext'
import { ManagerContext } from '@/contexts/ManagerContext'
import { setupManagerProvider } from '@/lib/utils/manager'
import { usePathname, useRouter } from 'next/navigation'
import { getLocalStorageAuth } from '@/lib/auth/auth'
import { fetcher } from '@/lib/api/fetch'
import { objectIdIsEmpty } from '@/lib/utils/common'
import { SettingsContext } from '@/contexts/SettingsContext'
import { Loader2 } from 'lucide-react'

import OrganizationModal from '@/components/common/modals/OrganizationModal'
import NoOrganization from '@/components/common/NoOrganization'
import MobileMenu from '@/app/manager/menu/MobileMenu'
import Sidebar from '@/app/manager/menu/Sidebar'
import TeamProvider from '@/components/common/TeamProvider'
import { SidebarProvider, SidebarInset } from '@/components/ui/shadcn/sidebar'

import { Toaster } from '@/components/ui/shadcn/sonner'
import './layout.css'

let billingInterval = null

export default function RootLayout({ children }) {
  const authContext = useContext(AuthContext)
  const [loading, setLoading] = useState(true)
  const [organizations, setOrganizations] = useState([])
  const [orgsFetched, setOrgsFetched] = useState(false)
  const [showOrgModal, setShowOrgModal] = useState(false)
  const [hasNoOrganization, setHasNoOrganization] = useState(false)
  const [pageSet, setPageSet] = useState(false)
  const [orgModalPage, setOrgModalPage] = useState('')
  const [billingDetails, setBillingDetails] = useState(null)
  const [currentUsage, setCurrentUsage] = useState(null)
  const [managerCache, setManagerCache] = useState({})
  const [roles, setRoles] = useState([])
  const [userGroups, setUserGroups] = useState([])
  const [fetchedOrgs, setFetchedOrgs] = useState()
  const [settingsFetched, setSettingsFetched] = useState(false)
  const pathname = usePathname()
  const router = useRouter()

  const managerActions = useMemo(
    () =>
      setupManagerProvider(
        setOrganizations,
        setOrgsFetched,
        setBillingDetails,
        setCurrentUsage,
        setManagerCache,
        managerCache,
        authContext,
      ),
    [authContext],
  )

  // Create stable settings actions with useCallback
  const settingsActions = useMemo(() => {
    return {
      getUserGroups: async () => {
        const res = await fetcher(
          `/api/admin/organization/${authContext.user.organization.id}/usergroups`,
          { method: 'GET' },
          authContext,
        )

        if (res.status !== 200) {
          console.error('Failed to get organizations for user')
          return
        }

        const data = await res.json()
        setUserGroups(data.userGroups || [])
      },
      getRoles: async () => {
        const res = await fetcher(
          `/api/admin/organization/${authContext.user.organization.id}/roles`,
          { method: 'GET' },
          authContext,
        )

        if (res.status !== 200) {
          console.error('Failed to get organizations for user')
          return
        }

        const data = await res.json()
        setRoles(data.roles || [])
      },
      setUserGroups: groups => setUserGroups(groups),
      setRoles: roles => setRoles(roles),
      addUserGroup: group => setUserGroups(g => [...g, group]),
      addRole: role => setRoles(r => [...r, role]),
      updateUserGroup: (id, group) =>
        setUserGroups(g => g.map(u => (u.id === id ? group : u))),
      updateRole: (id, role) =>
        setRoles(r => r.map(u => (u.id === id ? role : u))),
      removeUserGroup: id => setUserGroups(g => g.filter(u => u.id !== id)),
      removeRole: id => setRoles(r => r.filter(u => u.id !== id)),
    }
  }, [authContext.user?.organization?.id])

  const handleOrgs = useCallback(
    orgs => {
      if (!fetchedOrgs) setFetchedOrgs(orgs)

      // Check if the user has an organisation
      if (
        (!authContext.user.organization ||
          objectIdIsEmpty(authContext.user.organization.id)) &&
        !pageSet
      ) {
        // Check if there are any organisations the user belongs to
        if (!orgs || orgs.length === 0) {
          // No organizations available - show empty state instead of modal
          setHasNoOrganization(true)
        } else {
          // We have available organisations, prompt the user to choose one
          setShowOrgModal(true)
          setOrgModalPage('choose')
        }

        // Prevent this from happening every time
        setPageSet(true)
      }
    },
    [authContext.user, fetchedOrgs, pageSet],
  )

  const initOrgs = useCallback(async () => {
    // Check that the orgs have been fetched
    if (!orgsFetched) managerActions.getOrganizations(orgs => handleOrgs(orgs))
    else handleOrgs(fetchedOrgs)
  }, [orgsFetched, handleOrgs, managerActions, fetchedOrgs])

  useEffect(() => {
    if (authContext.user?.id) {
      initOrgs()
      // Only fetch roles/usergroups if user has a valid organization
      if (
        !settingsFetched &&
        authContext.user.organization &&
        !objectIdIsEmpty(authContext.user.organization.id)
      ) {
        settingsActions.getRoles()
        settingsActions.getUserGroups()
        setSettingsFetched(true)
      }
    }
  }, [authContext.user, initOrgs, settingsFetched, settingsActions])

  const checkToken = useCallback(() => {
    // If the user isn't logged in attempt to get the user from local storage
    const user = getLocalStorageAuth()
    if (user?.token) {
      // If the user is in local storage, set the user in the auth context
      authContext.setuser(user, () => {
        setLoading(false)
      })
      return
    }

    // We couldn't authenticate with stored data, redirect to login
    setLoading(false)

    router.push('/?from=' + pathname)
  }, [authContext, pathname, router])

  useEffect(() => {
    // Check if the user is logged in, if not redirect to login
    if (!authContext.isAuthenticated) {
      checkToken()
    } else {
      setLoading(false)
    }
  }, [authContext.isAuthenticated, checkToken])

  useEffect(() => {
    if (
      authContext.user.organization &&
      !objectIdIsEmpty(authContext.user.organization.id)
    ) {
      managerActions.getBilling(() => {})
      billingInterval = setInterval(
        () => managerActions.getBilling(() => {}),
        10000,
      )
    }
    return () => {
      if (billingInterval) clearInterval(billingInterval)
    }
  }, [authContext.user.organization, managerActions])

  const settingsValue = useMemo(
    () => ({ ...settingsActions, userGroups, roles }),
    [settingsActions, userGroups, roles],
  )

  const value = useMemo(
    () => ({
      ...managerActions,
      organizations,
      orgsFetched,
      billingDetails,
      currentUsage,
      managerCache,
    }),
    [
      managerActions,
      organizations,
      orgsFetched,
      billingDetails,
      currentUsage,
      managerCache,
    ],
  )

  const hideSidebar = useMemo(() => {
    return pathname.includes('/oauth')
  }, [pathname])

  // Show loading spinner for users without an org until we determine what to show
  const showOrgLoading =
    (!authContext.user.organization ||
      objectIdIsEmpty(authContext.user.organization.id)) &&
    !pageSet

  return (
    <ManagerContext.Provider value={value}>
      <SettingsContext.Provider value={settingsValue}>
        <TeamProvider>
          {showOrgLoading ? (
            <div className="flex items-center justify-center min-h-screen">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : hasNoOrganization ? (
            <NoOrganization />
          ) : hideSidebar ? (
            children
          ) : (
            <SidebarProvider>
              <Sidebar />
              <SidebarInset>
                <MobileMenu />
                {authContext.user.loaded && !loading ? children : null}
              </SidebarInset>
              <OrganizationModal
                open={showOrgModal}
                setOpen={setShowOrgModal}
                page={orgModalPage}
                setPage={setOrgModalPage}
                required
              />
            </SidebarProvider>
          )}
          <Toaster />
        </TeamProvider>
      </SettingsContext.Provider>
    </ManagerContext.Provider>
  )
}
